import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { stageTone, TONE_VARIANT } from '../../lib/value-tones.ts';
import { Badge, type BadgeVariant } from '../ui/badge.tsx';
import { Input } from '../ui/input.tsx';
import type { FilterOption, StageGroup } from './filter-bar.tsx';

/** Stage groups in the order they are listed; each is drawn as a pill of its outcome colour. */
export const STAGE_GROUPS: ReadonlyArray<{ group: StageGroup; label: string }> = [
  { group: 'open', label: 'Open' },
  { group: 'won', label: 'Won' },
  { group: 'lost', label: 'Lost' },
];

/** A branch of the option tree; with a `tone`, its label and its options are pills of that colour. */
export interface OptionGroup {
  group: string;
  label: string;
  tone?: BadgeVariant | undefined;
}

const STAGE_TREE: ReadonlyArray<OptionGroup> = STAGE_GROUPS.map((g) => ({
  ...g,
  tone: TONE_VARIANT[stageTone(g.group)],
}));

// A leading flag emoji would sort countries by ISO code instead of by name.
const sortKey = (label: string) => label.replace(/^[\u{1F1E6}-\u{1F1FF}]{2}\s*/u, '');

/** Options A→Z by label, except grouped ones (stages, template fields), which keep their order. */
export function sortOptions(options: ReadonlyArray<FilterOption>): FilterOption[] {
  return options.some((o) => o.group)
    ? [...options]
    : [...options].sort((a, b) => sortKey(a.label).localeCompare(sortKey(b.label)));
}

const rowClass =
  'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-(--v2-dropdown-bg-hover)';

function CheckItems({
  items,
  selected,
  onToggle,
  pill,
  single,
}: {
  items: ReadonlyArray<{ value: string; label: string; count?: number }>;
  selected: string[];
  onToggle: (value: string) => void;
  /** Draws each label in a pill of this colour (its group's tone). */
  pill?: BadgeVariant | undefined;
  /** One choice only ("is", "is not"): radios. */
  single?: boolean | undefined;
}) {
  return items.map((o) => (
    <li key={o.value}>
      <label className={rowClass}>
        <input
          type={single ? 'radio' : 'checkbox'}
          checked={selected.includes(o.value)}
          onChange={() => onToggle(o.value)}
        />
        {pill ? (
          <Badge variant={pill} className="min-w-0 truncate">
            {o.label}
          </Badge>
        ) : (
          <span className="truncate">{o.label}</span>
        )}
        {o.count !== undefined && (
          <span className="text-muted-foreground ml-auto text-xs">{o.count}</span>
        )}
      </label>
    </li>
  ));
}

/** A checkbox list of choices, shared by the quick filters and the condition builder. */
export function CheckList({
  label,
  items,
  selected,
  onToggle,
  single,
}: {
  label: string;
  items: ReadonlyArray<{ value: string; label: string; count?: number }>;
  selected: string[];
  onToggle: (value: string) => void;
  single?: boolean | undefined;
}) {
  if (items.length === 0) return null;
  return (
    <ul aria-label={label} className="max-h-60 overflow-auto">
      <CheckItems items={items} selected={selected} onToggle={onToggle} single={single} />
    </ul>
  );
}

/** Options under a tri-state checkbox per group; `matches` are the options the search left. */
function GroupTree({
  label,
  groups,
  options,
  matches,
  searching,
  folded: startFolded,
  single,
  selected,
  onChange,
  onToggle,
}: {
  label: string;
  groups: ReadonlyArray<OptionGroup>;
  options: ReadonlyArray<FilterOption>;
  matches: FilterOption[];
  /** While searching every group shows its matches, folded or not. */
  searching: boolean;
  /** Every group starts folded. */
  folded?: boolean | undefined;
  /** One option only: a group cannot be picked whole, so it has no checkbox. */
  single?: boolean | undefined;
  selected: string[];
  onChange: (next: string[]) => void;
  onToggle: (value: string) => void;
}) {
  const [folded, setFolded] = useState<ReadonlySet<string>>(
    () => new Set(startFolded ? groups.map((g) => g.group) : []),
  );
  const fold = (group: string) =>
    setFolded((f) => {
      const next = new Set(f);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  return (
    <ul aria-label={label} className="max-h-60 overflow-auto">
      {groups.map(({ group, label: name, tone }) => {
        const shown = matches.filter((o) => o.group === group);
        if (shown.length === 0) return null;
        const all = options.filter((o) => o.group === group).map((o) => o.value);
        const ticked = all.filter((v) => selected.includes(v)).length;
        const full = ticked === all.length;
        const open = searching || !folded.has(group);
        return (
          <li key={group}>
            <div className="flex items-center">
              <label className={`${rowClass} flex-1`}>
                {!single && (
                  <input
                    type="checkbox"
                    aria-label={name}
                    checked={full}
                    ref={(el) => {
                      if (el) el.indeterminate = ticked > 0 && !full;
                    }}
                    onChange={() =>
                      onChange(
                        full
                          ? selected.filter((v) => !all.includes(v))
                          : [...selected, ...all.filter((v) => !selected.includes(v))],
                      )
                    }
                  />
                )}
                {tone ? <Badge variant={tone}>{name}</Badge> : <span>{name}</span>}
                <span className="text-muted-foreground text-xs">
                  {ticked > 0 ? `${ticked}/${all.length}` : all.length}
                </span>
              </label>
              <button
                type="button"
                aria-expanded={open}
                aria-label={`${open ? 'Collapse' : 'Expand'} ${name}`}
                disabled={searching}
                onClick={() => fold(group)}
                className="text-muted-foreground rounded-md p-1 hover:bg-(--v2-dropdown-bg-hover) disabled:opacity-40"
              >
                {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
              </button>
            </div>
            {open && (
              <ul aria-label={name} className="pl-5">
                <CheckItems
                  items={shown}
                  selected={selected}
                  onToggle={onToggle}
                  pill={tone}
                  single={single}
                />
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The choices with a search box that narrows them as you type. */
export function OptionSearch({
  label,
  options,
  selected,
  onChange,
  single,
  groups = STAGE_TREE,
  folded,
}: {
  label: string;
  options: ReadonlyArray<FilterOption>;
  selected: string[];
  onChange: (next: string[]) => void;
  /** One choice only: picking replaces the previous one. */
  single?: boolean | undefined;
  /** The tree's branches for options with a `group`; stages by default. */
  groups?: ReadonlyArray<OptionGroup> | undefined;
  /** The tree's groups start folded. */
  folded?: boolean | undefined;
}) {
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const matches = sortOptions(options).filter((o) => o.label.toLowerCase().includes(needle));
  const toggle = (v: string) =>
    onChange(
      single ? [v] : selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v],
    );
  const grouped = options.some((o) => o.group);
  return (
    <>
      <Input
        aria-label={`Search ${label}`}
        placeholder={`Search ${label}`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {matches.length === 0 && <p className="text-muted-foreground px-2 text-xs">No matches</p>}
      {grouped && (
        <GroupTree
          label={`${label} options`}
          groups={groups}
          options={options}
          matches={matches}
          searching={needle !== ''}
          folded={folded}
          single={single}
          selected={selected}
          onChange={onChange}
          onToggle={toggle}
        />
      )}
      <CheckList
        label={grouped ? `Other ${label} options` : `${label} options`}
        items={grouped ? matches.filter((o) => !o.group) : matches}
        selected={selected}
        onToggle={toggle}
        single={single}
      />
    </>
  );
}
